import React, { useState, useEffect, StrictMode, useCallback } from 'react';
import { Table, Button, Modal, Form, Input, Select, Space, message, Popconfirm, Collapse, Tag, Tabs, InputNumber, Tooltip } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SettingOutlined, CheckOutlined, StopOutlined, MenuOutlined, EyeOutlined, EyeInvisibleOutlined, OrderedListOutlined, LinkOutlined, InfoCircleOutlined, SearchOutlined, CodeOutlined } from '@ant-design/icons';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import api from '../../services/api';
import './index.css';
import { useParams, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import axios from 'axios';

const MetricItem = React.memo(({ metric, onRemove, onCodeChange, code, variables, brandId, dragHandleProps, metricCodeList, loading, metricVisibility, handleMetricVisibilityChange }) => {
  const [selectedMetricCode, setSelectedMetricCode] = useState(null);
  const [form] = Form.useForm();

  // Form değerlerini ve metrik detaylarını yükle
  useEffect(() => {
    if (!code) {
      setSelectedMetricCode(null);
      return;
    }

    form.setFieldsValue({
      code,
      variables: variables || {}
    });

    // Seçili metrik kodunun detaylarını bul
    const selectedCode = metricCodeList.find(mc => mc.code === code);
    if (selectedCode) {
      setSelectedMetricCode(selectedCode);
    }
  }, [code, variables, form, metricCodeList]);

  // Event Handlers
  const handleCodeSelect = useCallback(async (selectedCode) => {
    if (!selectedCode) {
      setSelectedMetricCode(null);
      return;
    }

    const selectedCodeData = metricCodeList.find(mc => mc.code === selectedCode);
    if (selectedCodeData) {
      setSelectedMetricCode(selectedCodeData);
    }
  }, [metricCodeList]);

  const handleSelectChange = useCallback((value) => {
    if (value === undefined) {
      setSelectedMetricCode(null);
    }
    const codeValue = typeof value === 'object' ? value.code : value;
    onCodeChange(metric.id, codeValue);
  }, [metric.id, onCodeChange]);

  const handleFormValuesChange = useCallback((changedValues, allValues) => {
    if (changedValues.variables) {
      const updatedVariables = {
        ...(variables || {}),
        ...changedValues.variables
      };
      onCodeChange(metric.id, allValues.code, updatedVariables);
    }
  }, [metric.id, onCodeChange, variables]);

  // Render Functions
  const renderVariables = useCallback(() => {
    if (!selectedMetricCode || !selectedMetricCode.variables || selectedMetricCode.variables.length === 0) {
      return null;
    }

    return (
      <Collapse defaultActiveKey={['variables']}>
        <Collapse.Panel header="Değişkenler" key="variables">
          {selectedMetricCode.variables.map((variable, index) => (
            <Form.Item
              key={index}
              label={variable.name}
              name={['variables', variable.name]}
              rules={[{ required: true, message: `${variable.name} değişkeni gereklidir` }]}
            >
              <Input placeholder={variable.description || variable.name} />
            </Form.Item>
          ))}
        </Collapse.Panel>
      </Collapse>
    );
  }, [selectedMetricCode]);

  const renderMetricCodeSelect = useCallback(() => (
    <Form.Item name="code">
      <Select
        placeholder="Metrik Kodu"
        value={code}
        onChange={handleSelectChange}
        onSelect={handleCodeSelect}
        style={{ width: 200 }}
        loading={loading}
        allowClear
        showSearch
        optionFilterProp="children"
        filterOption={(input, option) =>
          option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
        }
      >
        {Array.isArray(metricCodeList) && metricCodeList.map(mc => (
          <Select.Option key={mc.id} value={mc.code}>
            {mc.code} {mc.description ? `(${mc.description})` : ''}
          </Select.Option>
        ))}
      </Select>
    </Form.Item>
  ), [code, loading, metricCodeList, handleSelectChange, handleCodeSelect]);

  return (
    <div className="metric-item">
      <div className="metric-item-drag-handle" {...dragHandleProps}>
        <MenuOutlined style={{ color: '#999' }} />
      </div>
      <div className="metric-info">
        <div className="metric-name">{metric.name}</div>
        <Form
          form={form}
          initialValues={{ code, variables }}
          onValuesChange={handleFormValuesChange}
          layout="horizontal"
          style={{ flex: 1 }}
        >
          <Space style={{ width: '100%', justifyContent: 'flex-start' }}>
            {renderMetricCodeSelect()}
            {code && renderVariables()}
          </Space>
        </Form>
      </div>
      <div>
        <Button
          type="text"
          style={{ marginRight: 8 }}
          icon={metricVisibility[metric.id] ? <EyeOutlined /> : <EyeInvisibleOutlined />}
          onClick={() => handleMetricVisibilityChange(metric.id, !metricVisibility[metric.id])}
          title={metricVisibility[metric.id] ? "Gizle" : "Göster"}
        />
        <Button
          type="text"
          className="remove-button"
          icon={<DeleteOutlined />}
          onClick={() => onRemove(metric.id)}
        />
      </div>
    </div>
  );
});

// PropTypes ekleyelim
MetricItem.propTypes = {
  metric: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired,
  onRemove: PropTypes.func.isRequired,
  onCodeChange: PropTypes.func.isRequired,
  code: PropTypes.string,
  variables: PropTypes.object,
  brandId: PropTypes.number,
  dragHandleProps: PropTypes.object,
  metricCodeList: PropTypes.array,
  loading: PropTypes.bool,
  metricVisibility: PropTypes.object,
  handleMetricVisibilityChange: PropTypes.func
};

const MetricList = React.memo(({ metrics = [], selectedMetricIds = [], onRemove, onCodeChange, metricCodes = {}, metricCodeList = [], loading = false, metricVisibility, handleMetricVisibilityChange }) => {
  const [enabled, setEnabled] = React.useState(false);

  React.useEffect(() => {
    const animation = requestAnimationFrame(() => setEnabled(true));
    return () => {
      cancelAnimationFrame(animation);
      setEnabled(false);
    };
  }, []);

  if (!enabled) {
    return null;
  }

  return (
    <StrictMode>
      <Droppable droppableId="metrics" type="METRIC">
        {(provided) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className="metric-list"
          >
            {selectedMetricIds.map((metricId, index) => {
              const metric = metrics.find(m => m.id === metricId);
              const metricCode = metricCodes[metricId] || {};
              if (!metric) return null;
              
              return (
                <Draggable key={metricId} draggableId={String(metricId)} index={index}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      style={{
                        ...provided.draggableProps.style,
                        background: snapshot.isDragging ? '#f0f0f0' : 'white',
                        marginBottom: '8px'
                      }}
                    >
                      <MetricItem
                        metric={metric}
                        onRemove={onRemove}
                        onCodeChange={onCodeChange}
                        code={metricCode.code}
                        variables={metricCode.variables}
                        brandId={metric.BrandMetric?.brandId}
                        dragHandleProps={provided.dragHandleProps}
                        metricCodeList={metricCodeList}
                        loading={loading}
                        metricVisibility={metricVisibility}
                        handleMetricVisibilityChange={handleMetricVisibilityChange}
                      />
                    </div>
                  )}
                </Draggable>
              );
            })}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </StrictMode>
  );
});

const BrandSettings = () => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active');
  const [orderModalVisible, setOrderModalVisible] = useState(false);
  const [brandOrderItems, setBrandOrderItems] = useState([]);
  const [allBrandsTokenLoading, setAllBrandsTokenLoading] = useState(false);
  const [advisors, setAdvisors] = useState([]);
  const [advisorsLoading, setAdvisorsLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [brandTokenLoadingIds, setBrandTokenLoadingIds] = useState([]);
  const [allIndividualTokensLoading, setAllIndividualTokensLoading] = useState(false);
  const navigate = useNavigate();

  // Advisor listesini yükle
  useEffect(() => {
    const fetchAdvisors = async () => {
      try {
        setAdvisorsLoading(true);
        const response = await api.get('/advisors');
        setAdvisors(response.data || []);
      } catch (error) {
        console.error('Advisor listesi yüklenirken hata:', error);
        setAdvisors([]); // Hata durumunda boş liste
      } finally {
        setAdvisorsLoading(false);
      }
    };
    fetchAdvisors();
  }, []);

  // Markaları yükle
  const fetchBrands = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/brands', {
        params: {
          includeDeleted: true // Aktif ve pasif tüm markaları getir
        }
      });
      
      if (!response.data || !Array.isArray(response.data)) {
        console.error('Beklenmeyen API yanıtı:', response.data);
        message.error('Markalar yüklenirken bir hata oluştu');
        setBrands([]);
        return;
      }

      const sortedBrands = response.data
        .filter(brand => brand && typeof brand === 'object') // Veri bütünlüğünü kontrol et
        .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)); // displayOrder'a göre sırala

      setBrands(sortedBrands);
    } catch (error) {
      console.error('Marka yükleme hatası:', error.response || error);
      message.error('Markalar yüklenirken bir hata oluştu');
      setBrands([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBrands();
  }, [fetchBrands]);

  const handleDelete = async (record) => {
    try {
      const response = await api.delete(`/brands/${record.id}`);
      if (response.status === 200) {
        message.success(response.data.message || 'Marka başarıyla pasife alındı');
        fetchBrands();
      }
    } catch (error) {
      message.error(error.response?.data?.error || 'Marka pasife alınırken bir hata oluştu');
    }
  };

  const handleActivate = async (record) => {
    try {
      const response = await api.post(`/brands/${record.id}/activate`);
      if (response.status === 200) {
        message.success(response.data.message || 'Marka başarıyla aktif hale getirildi');
        fetchBrands();
      }
    } catch (error) {
      message.error(error.response?.data?.error || 'Marka aktifleştirilirken bir hata oluştu');
      console.error('Marka aktifleştirme hatası:', error);
    }
  };

  // Marka Sıralama İşlemleri
  const onBrandDragEnd = async (result) => {
    const { source, destination } = result;
    if (!destination || (destination.droppableId === source.droppableId && destination.index === source.index)) {
      return;
    }

    const items = Array.from(brandOrderItems);
    const [reorderedItem] = items.splice(source.index, 1);
    items.splice(destination.index, 0, reorderedItem);

    // Sıralamayı güncelle (Sadece state'i güncelle, kaydetme OK butonunda)
    setBrandOrderItems(items);
  };

  const handleOrderModalOk = async () => {
    try {
      const brandIdsPayload = brandOrderItems.map((brand, index) => ({
        id: brand.id,
        displayOrder: index + 1
      }));

      await api.put('/brands/order', { brandIds: brandIdsPayload });
      message.success('Marka sıralaması başarıyla güncellendi');
      setOrderModalVisible(false);
      fetchBrands(); // Ana listeyi yenile
    } catch (error) {
      console.error('Sıralama güncellenirken hata:', error);
      message.error('Marka sıralaması güncellenirken bir hata oluştu');
    }
  };

  const openOrderModal = () => {
      const activeBrandsOrdered = brands
          .filter(brand => !brand.isDeleted)
          .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
          .map(brand => ({ // Sadece gerekli alanları al
              id: brand.id,
              name: brand.name,
              displayOrder: brand.displayOrder || 0
          }));
      setBrandOrderItems(activeBrandsOrdered);
      setOrderModalVisible(true);
  };

  // Token Oluşturma
  const generateAllBrandsEmbedToken = async () => {
    try {
      setAllBrandsTokenLoading(true);
      const response = await api.post('/embed/all-brands-token');
      const { embedUrl, brandCount } = response.data;
      
      Modal.success({
        title: `Tüm Markalar İçin Embed Token Oluşturuldu`,
        content: (
          <div>
            <p>{brandCount} aktif marka için embed token oluşturuldu.</p>
            <p>Bu token ile tüm markaların verileri harici sistemlerde görüntülenebilir.</p>
            <p style={{ wordBreak: 'break-all', marginTop: 10 }}>
              <strong>Embed URL:</strong><br />
              {embedUrl}
            </p>
            <Button 
              type="primary" 
              style={{ marginTop: 10 }}
              onClick={() => {
                navigator.clipboard.writeText(embedUrl);
                message.success('URL panoya kopyalandı!');
              }}
            >
              URL'yi Kopyala
            </Button>
          </div>
        ),
        width: 600,
      });
    } catch (error) {
      message.error('Token oluşturulurken bir hata oluştu!');
      console.error('Tüm markalar için token oluşturma hatası:', error);
    } finally {
      setAllBrandsTokenLoading(false);
    }
  };

  // Tek marka için embed token oluşturma
  const generateBrandEmbedToken = async (brand) => {
    try {
      setBrandTokenLoadingIds(prev => [...prev, brand.id]);
      const response = await api.post('/embed/brand-token', {
        brandId: brand.id,
        expiresIn: '90d'
      });
      const { embedUrl, brandName } = response.data;
      
      Modal.success({
        title: `${brandName} İçin Embed Token Oluşturuldu`,
        content: (
          <div>
            <p>Bu token ile sadece {brandName} markasının verileri harici sistemlerde görüntülenebilir.</p>
            <p style={{ wordBreak: 'break-all', marginTop: 10 }}>
              <strong>Embed URL:</strong><br />
              {embedUrl}
            </p>
            <Button 
              type="primary" 
              style={{ marginTop: 10 }}
              onClick={() => {
                navigator.clipboard.writeText(embedUrl);
                message.success('URL panoya kopyalandı!');
              }}
            >
              URL'yi Kopyala
            </Button>
          </div>
        ),
        width: 600,
      });
    } catch (error) {
      message.error('Token oluşturulurken bir hata oluştu!');
      console.error('Marka için token oluşturma hatası:', error);
    } finally {
      setBrandTokenLoadingIds(prev => prev.filter(id => id !== brand.id));
    }
  };

  // Tüm markalar için ayrı ayrı token oluşturma
  const generateAllIndividualTokens = async () => {
    try {
      setAllIndividualTokensLoading(true);
      const response = await api.post('/embed/all-brands-single-tokens', {
        expiresIn: '90d'
      });
      const { tokens, brandCount } = response.data;
      
      // Token listesini bir tabloda göster
      Modal.info({
        title: `${brandCount} Marka İçin Ayrı Tokenlar Oluşturuldu`,
        width: 800,
        content: (
          <div>
            <p style={{ marginBottom: 16 }}>Her marka için ayrı embed tokenları oluşturuldu. Aşağıdan ilgili markanın URL'sini kopyalayabilirsiniz.</p>
            <Table
              dataSource={tokens}
              rowKey="brandId"
              size="small"
              pagination={{ pageSize: 10 }}
              columns={[
                {
                  title: 'Marka',
                  dataIndex: 'brandName',
                  key: 'brandName',
                  width: 200,
                },
                {
                  title: 'Embed URL',
                  dataIndex: 'embedUrl',
                  key: 'embedUrl',
                  render: (url) => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ flex: 1, wordBreak: 'break-all', fontSize: 12 }}>{url}</span>
                      <Button
                        size="small"
                        icon={<LinkOutlined />}
                        onClick={() => {
                          navigator.clipboard.writeText(url);
                          message.success('URL kopyalandı!');
                        }}
                      >
                        Kopyala
                      </Button>
                    </div>
                  ),
                },
              ]}
            />
          </div>
        ),
      });
    } catch (error) {
      message.error('Tokenlar oluşturulurken bir hata oluştu!');
      console.error('Tüm markalar için ayrı token oluşturma hatası:', error);
    } finally {
      setAllIndividualTokensLoading(false);
    }
  };

  // Aktif ve Pasif markalar için filtreleme
  const activeBrands = brands.filter(brand => !brand.isDeleted);
  const passiveBrands = brands.filter(brand => brand.isDeleted);

  // Arama metni ile filtreleme
  const filterBrandsBySearch = (brandList) => {
    if (!searchText.trim()) {
      return brandList;
    }
    
    const searchTerm = searchText.toLowerCase();
    return brandList.filter(brand => 
      brand.name?.toLowerCase().includes(searchTerm) ||
      brand.template?.toLowerCase().includes(searchTerm) ||
      brand.advisor?.toLowerCase().includes(searchTerm) ||
      brand.growth?.toLowerCase().includes(searchTerm) ||
      brand.assistant?.toLowerCase().includes(searchTerm) ||
      brand.meta?.toLowerCase().includes(searchTerm) ||
      brand.google?.toLowerCase().includes(searchTerm) ||
      brand.seo?.toLowerCase().includes(searchTerm)
    );
  };

  const filteredActiveBrands = filterBrandsBySearch(activeBrands);
  const filteredPassiveBrands = filterBrandsBySearch(passiveBrands);

  // Tablo Sütunları
  const columns = [
    // Sıralama kolonu sadece aktif markalar tablosunda görünür
    ...(activeTab === 'active' ? [{
        key: 'dragHandle',
        title: ' ',
        width: 30,
        className: 'drag-visible',
        // Sıralama modal'ı açıkken sürükleme ikonu göstermiyoruz
        render: () => orderModalVisible ? null : <MenuOutlined style={{ cursor: 'grab', color: '#999' }} />,
    }] : []),
    {
      title: 'Marka Adı',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Para Birimi',
      dataIndex: 'currency',
      key: 'currency',
      render: (text) => text || 'TRY',
    },
    {
      title: 'Şablon',
      dataIndex: 'template',
      key: 'template',
      render: (text) => text || '-',
      filters: [
        { text: 'WEB AUTOBUDGET', value: 'WEB AUTOBUDGET' },
        { text: 'WEB MANUELBUDGET', value: 'WEB MANUELBUDGET' },
        { text: 'TRENDYOL', value: 'TRENDYOL' },
      ],
      onFilter: (value, record) => record.template === value,
    },
    {
      title: 'Danışman',
      dataIndex: 'advisor',
      key: 'advisor',
      render: (text) => text || '-',
      // Filtreleri yüklenince ekle
      filters: advisorsLoading ? [] : advisors.map(advisor => ({ 
          text: advisor.advisor_name, 
          value: advisor.advisor_name 
      })),
      onFilter: (value, record) => record.advisor === value,
    },
    {
      title: 'Growth',
      dataIndex: 'growth',
      key: 'growth',
      render: (text) => text || '-',
      filters: advisorsLoading ? [] : advisors.map(advisor => ({ 
          text: advisor.advisor_name, 
          value: advisor.advisor_name 
      })),
      onFilter: (value, record) => record.growth === value,
    },
    {
      title: 'Assistant',
      dataIndex: 'assistant',
      key: 'assistant',
      render: (text) => text || '-',
      filters: advisorsLoading ? [] : advisors.map(advisor => ({ 
          text: advisor.advisor_name, 
          value: advisor.advisor_name 
      })),
      onFilter: (value, record) => record.assistant === value,
    },
    {
      title: 'Meta/Facebook',
      dataIndex: 'meta',
      key: 'meta',
      render: (text) => text || '-',
      filters: advisorsLoading ? [] : advisors.map(advisor => ({ 
          text: advisor.advisor_name, 
          value: advisor.advisor_name 
      })),
      onFilter: (value, record) => record.meta === value,
    },
    {
      title: 'Google Ads',
      dataIndex: 'google',
      key: 'google',
      render: (text) => text || '-',
      filters: advisorsLoading ? [] : advisors.map(advisor => ({ 
          text: advisor.advisor_name, 
          value: advisor.advisor_name 
      })),
      onFilter: (value, record) => record.google === value,
    },
    {
      title: 'SEO',
      dataIndex: 'seo',
      key: 'seo',
      render: (text) => text || '-',
      filters: advisorsLoading ? [] : advisors.map(advisor => ({ 
          text: advisor.advisor_name, 
          value: advisor.advisor_name 
      })),
      onFilter: (value, record) => record.seo === value,
    },
    {
      title: 'Durum',
      dataIndex: 'isDeleted',
      key: 'status',
      render: (isDeleted) => (
        <Tag color={isDeleted ? 'red' : 'green'}>
          {isDeleted ? 'Pasif' : 'Aktif'}
        </Tag>
      ),
      // Duruma göre filtreleme ekle
       filters: [
         { text: 'Aktif', value: false },
         { text: 'Pasif', value: true },
       ],
       // Filter only on the combined view if we had one, 
       // but handled by Tabs now, so onFilter might not be needed unless tabs are removed.
       onFilter: (value, record) => record.isDeleted === value,
    },
    {
      title: 'İşlemler',
      key: 'action',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Düzenle / Metrik Ayarları">
              <Button
                icon={<EditOutlined />}
                onClick={() => navigate(`/brands/${record.id}/edit`)}
                size="small"
                disabled={record.isDeleted} // Pasif markalar düzenlenemez
              />
          </Tooltip>
          {/* Embed Token Butonu - Sadece aktif markalar için */}
          {!record.isDeleted && (
            <Tooltip title="Embed Kodu Oluştur">
              <Button
                icon={<CodeOutlined />}
                onClick={() => generateBrandEmbedToken(record)}
                size="small"
                loading={brandTokenLoadingIds.includes(record.id)}
              />
            </Tooltip>
          )}
          {/* Silme/Aktifleştirme Butonları */} 
          {!record.isDeleted ? (
            <Popconfirm
              title="Bu markayı pasife almak istediğinize emin misiniz?"
              onConfirm={() => handleDelete(record)}
              okText="Evet"
              cancelText="Hayır"
              disabled={record.isDeleted}
            >
              <Tooltip title="Pasife Al">
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    size="small"
                    disabled={record.isDeleted}
                  />
              </Tooltip>
            </Popconfirm>
          ) : (
            <Popconfirm
              title="Bu markayı aktifleştirmek istediğinize emin misiniz?"
              onConfirm={() => handleActivate(record)}
              okText="Evet"
              cancelText="Hayır"
            >
               <Tooltip title="Aktifleştir">
                  <Button
                    type="primary"
                    icon={<CheckOutlined />}
                    style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                    size="small"
                  />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <h2 style={{ marginBottom: 16 }}>Marka Ayarları</h2>
      
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Space>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/brands/new')} // Yeni marka sayfasına yönlendir
          >
            Yeni Marka
          </Button>
          <Button
            icon={<OrderedListOutlined />}
            onClick={openOrderModal}
          >
            Sıralamayı Düzenle
          </Button>
          <Button
            icon={<LinkOutlined />}
            onClick={generateAllBrandsEmbedToken}
            loading={allBrandsTokenLoading}
          >
            Tüm Markalar İçin Token
          </Button>
          <Button
            icon={<CodeOutlined />}
            onClick={generateAllIndividualTokens}
            loading={allIndividualTokensLoading}
          >
            Her Marka İçin Ayrı Token
          </Button>
        </Space>
        
        {/* Arama kutusu sağ tarafta */}
        <Input
          placeholder="Marka ara... (marka adı, şablon, danışman, growth, assistant, meta, google, seo)"
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          allowClear
          style={{ width: 500 }}
        />
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: 'active',
            label: `Aktif Markalar (${filteredActiveBrands.length})`,
            children: (
              <Table
                columns={columns.filter(col => col.key !== 'status')} // Durum kolonunu aktif tablosunda gösterme
                dataSource={filteredActiveBrands}
                rowKey="id"
                loading={loading}
                pagination={{
                  defaultPageSize: 10,
                  showSizeChanger: true,
                  showTotal: (total, range) => {
                    const totalActive = activeBrands.length;
                    const searchInfo = searchText.trim() ? ` (${totalActive} aktif markadan filtrelendi)` : '';
                    return `${range[0]}-${range[1]} / ${total} aktif marka${searchInfo}`;
                  }
                }}
              />
            ),
          },
          {
            key: 'passive',
            label: `Pasif Markalar (${filteredPassiveBrands.length})`,
            children: (
              <Table
                // Pasif tablosunda sürükleme kolonu olmasın ve durum kolonu olsun
                columns={columns.filter(col => col.key !== 'dragHandle')}
                dataSource={filteredPassiveBrands}
                rowKey="id"
                loading={loading}
                pagination={{
                  defaultPageSize: 10,
                  showSizeChanger: true,
                  showTotal: (total, range) => {
                    const totalPassive = passiveBrands.length;
                    const searchInfo = searchText.trim() ? ` (${totalPassive} pasif markadan filtrelendi)` : '';
                    return `${range[0]}-${range[1]} / ${total} pasif marka${searchInfo}`;
                  }
                }}
              />
            ),
          },
        ]}
      />

      {/* Marka Sıralama Modalı */}
      <Modal
        title="Aktif Marka Sıralaması"
        open={orderModalVisible}
        onOk={handleOrderModalOk}
        onCancel={() => {
          setOrderModalVisible(false);
          // fetchBrands(); // İptalde listeyi yenilemeye gerek yok, OK ile yenileniyor.
        }}
        width={600}
        destroyOnClose
      >
        <div style={{ marginBottom: 16 }}>
          <p>Aktif markaları sıralamak için sürükleyip bırakın. Yeni sıralama kaydedildiğinde geçerli olacaktır.</p>
        </div>
        <DragDropContext onDragEnd={onBrandDragEnd}>
          <Droppable droppableId="brands-order">
            {(provided) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                style={{
                  maxHeight: '60vh',
                  overflowY: 'auto',
                  padding: '8px',
                  background: '#fafafa',
                  borderRadius: '4px'
                }}
              >
                {brandOrderItems.map((brand, index) => (
                  <Draggable key={brand.id} draggableId={String(brand.id)} index={index}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={{
                          ...provided.draggableProps.style,
                          userSelect: 'none',
                          padding: '8px 12px',
                          margin: '0 0 8px 0',
                          background: snapshot.isDragging ? '#e6f7ff' : 'white',
                          border: '1px solid #f0f0f0',
                          borderRadius: '4px',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <Space>
                           <MenuOutlined style={{ color: '#999', cursor: 'grab' }} />
                           <span>{brand.name}</span>
                           <Tag color="blue">Yeni Sıra: {index + 1}</Tag>
                        </Space>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </Modal>

    </div>
  );
};

export default BrandSettings; 